import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, TrendingUp, Newspaper, GraduationCap, User, LogOut, MessageSquare, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// FonderX Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_forex-compare-2/artifacts/l16i8o9b_FONDER%20CURRENCY%20EXCHANGE-4.png";

// Main navigation links
const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/heroes", label: "Heroes Rate", isNew: true },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
];

// Crypto dropdown items
const cryptoLinks = [
  { href: "/crypto", label: "Live Prices", icon: TrendingUp, desc: "Real-time crypto rates" },
  { href: "/crypto/news", label: "Crypto News", icon: Newspaper, desc: "Latest market updates" },
  { href: "/crypto/academy", label: "FonderX Academy", icon: GraduationCap, desc: "Learn cryptocurrency" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [cryptoDropdownOpen, setCryptoDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [mobileCryptoOpen, setMobileCryptoOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  
  const isActive = (path) => location.pathname === path;
  const isCryptoActive = location.pathname.startsWith('/crypto');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCryptoDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setCryptoDropdownOpen(false);
    setUserDropdownOpen(false);
    setIsOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    setUserDropdownOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 navbar-dark-blue" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="FonderX Currency Exchange" 
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7">
            <Link
              to="/"
              className={`nav-link ${isActive("/") ? "nav-link-active" : ""}`}
            >
              Home
            </Link>
            
            {/* Crypto Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setCryptoDropdownOpen(!cryptoDropdownOpen)}
                className={`nav-link flex items-center gap-1 ${isCryptoActive ? "nav-link-active" : ""}`}
                data-testid="crypto-dropdown-btn"
              >
                Crypto
                <ChevronDown className={`w-4 h-4 transition-transform ${cryptoDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {cryptoDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-[#0a1628] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50">
                  {cryptoLinks.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${
                        isActive(item.href) ? 'bg-[#3B82F6]/10 text-[#3B82F6]' : 'text-white/80'
                      }`}
                      data-testid={`crypto-dropdown-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                        isActive(item.href) ? 'bg-[#3B82F6]/20' : 'bg-white/5'
                      }`}>
                        <item.icon className={`w-5 h-5 ${isActive(item.href) ? 'text-[#3B82F6]' : 'text-white/60'}`} />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isActive(item.href) ? 'text-[#3B82F6]' : 'text-white'}`}>
                          {item.label}
                        </p>
                        <p className="text-white/40 text-xs">{item.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* AI Predictor - NEW */}
            <Link
              to="/ai-predictor"
              className={`nav-link ${isActive("/ai-predictor") ? "nav-link-active" : ""}`}
              data-testid="ai-predictor-link"
            >
              AI Predictor
            </Link>
            
            {/* Other nav links */}
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link ${link.isNew ? "flex items-center gap-1.5 whitespace-nowrap" : ""} ${isActive(link.href) ? "nav-link-active" : ""}`}
              >
                {link.label}
                {link.isNew && <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-400 text-[#001435] rounded">NEW</span>}
              </Link>
            ))}
          </div>

          {/* Right side - Auth/User */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-[#3B82F6] flex items-center justify-center text-white text-sm font-semibold">
                    {user?.first_name?.charAt(0)}
                  </div>
                  <span className="text-white text-sm hidden xl:block">{user?.first_name}</span>
                  <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {userDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-48 bg-[#0a1628] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50">
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-white/80 transition-colors"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm">Dashboard</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-red-400 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Log Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/auth?mode=login"
                  className="flex items-center gap-1.5 text-white/80 hover:text-white px-3 py-2 rounded-lg transition-colors text-sm font-medium"
                  data-testid="signin-btn"
                >
                  <User className="w-4 h-4" />
                  Sign In
                </Link>
                <Link
                  to="/auth?mode=register"
                  className="flex items-center gap-1.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  data-testid="signup-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white/80 hover:text-white transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#0a1628]">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className={`block py-3 px-4 rounded-lg transition-colors ${
                isActive("/") ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            
            {/* Mobile Crypto Accordion */}
            <div>
              <button
                onClick={() => setMobileCryptoOpen(!mobileCryptoOpen)}
                className={`w-full flex items-center justify-between py-3 px-4 rounded-lg transition-colors ${
                  isCryptoActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`}
              >
                <span>Crypto</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileCryptoOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileCryptoOpen && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/10 pl-4">
                  {cryptoLinks.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors ${
                        isActive(item.href) ? "bg-[#3B82F6]/10 text-[#3B82F6]" : "text-white/60 hover:bg-white/5 hover:text-white"
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* AI Predictor - Mobile */}
            <Link
              to="/ai-predictor"
              className={`block py-3 px-4 rounded-lg transition-colors ${
                isActive("/ai-predictor") ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
              onClick={() => setIsOpen(false)}
            >
              AI Predictor
            </Link>
            
            {navLinks.slice(1).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`${link.isNew ? "flex items-center gap-2" : "block"} py-3 px-4 rounded-lg transition-colors ${
                  isActive(link.href) ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
                {link.isNew && <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-amber-400 text-[#001435] rounded">NEW</span>}
              </Link>
            ))}
            
            {/* Auth Section */}
            <div className="border-t border-white/10 mt-2 pt-2">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-2 py-3 px-4 rounded-lg transition-colors ${
                      isActive("/dashboard") ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    My Dashboard
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                    className="w-full flex items-center gap-2 py-3 px-4 rounded-lg text-red-400 hover:bg-white/5 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </button>
                </>
              ) : (
                <div className="space-y-2 pt-2">
                  <Link
                    to="/auth?mode=login"
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-white/20 text-white hover:bg-white/5 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    Sign In
                  </Link>
                  <Link
                    to="/auth?mode=register"
                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <UserPlus className="w-4 h-4" />
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
